import { useEffect, useLayoutEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

export const XS = 480;
export const SM = 640;
export const MD = 900;
export const LG = 1200;
export const XL = 1440;
export const XXL = 1728;

export const widthLimit = 400;

const getWindowDimensions = () => {
  const width = window.innerWidth;
  const height = window.innerHeight;
  return {
    width,
    height,
    size: getSize(width),
    isDesktop: width >= SM,
    inLimit: width && width > widthLimit,
  };
};

const getSize = (width: number | null) => {
  if (width) {
    if (width < 481) {
      return XS;
    }
    if (width < 601) {
      return SM;
    }
    if (width < 901) {
      return MD;
    }
    if (width < 1200) {
      return LG;
    }
    if (width < 1440) {
      return XL;
    } else {
      return XXL;
    }
  } else {
    return undefined;
  }
};

export default function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  const handleResize = useDebouncedCallback(() => {
    setWindowDimensions(getWindowDimensions());
  }, 250);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  return windowDimensions;
}

const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < SM);

  const updateSize = useDebouncedCallback(() => {
    setIsMobile(window.innerWidth < SM);
  }, 250);

  useLayoutEffect(() => {
    window.addEventListener('resize', updateSize);
    return (): void => window.removeEventListener('resize', updateSize);
  }, [updateSize]);

  return isMobile;
};

const useIsTablet = (): boolean => {
  const [isIpad, setIsIpad] = useState(window.innerWidth < MD);

  const updateSize = useDebouncedCallback(() => {
    setIsIpad(window.innerWidth < MD);
  }, 250);

  useEffect(() => {
    window.addEventListener('resize', updateSize);
    return (): void => window.removeEventListener('resize', updateSize);
  }, [updateSize]);

  return isIpad;
};

const CANVAS_BREAKPOINT = 1024;

const useCanShowCanvasPanel = (): boolean => {
  const [canShow, setCanShow] = useState(window.innerWidth >= CANVAS_BREAKPOINT);

  const updateSize = useDebouncedCallback(() => {
    setCanShow(window.innerWidth >= CANVAS_BREAKPOINT);
  }, 250);

  useLayoutEffect(() => {
    window.addEventListener('resize', updateSize);
    return (): void => window.removeEventListener('resize', updateSize);
  }, [updateSize]);

  return canShow;
};

export { useCanShowCanvasPanel, useIsMobile, useIsTablet };
